import React, { FC } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface ShowcaseGalleryDialogProps {
  open: boolean;
  onClose: () => void;
  isLoadingCandidates: boolean;
  candidates: any[];
  candidatePage: number;
  setCandidatePage: React.Dispatch<React.SetStateAction<number>>;
  totalCandidates: number;
  selectedCandidates: Record<string, boolean>;
  setSelectedCandidates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  candidateCaptions: Record<string, string>;
  setCandidateCaptions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  candidateFileNames: Record<string, string>;
  setCandidateFileNames: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onSave: () => void;
}

const ShowcaseGalleryDialog: FC<ShowcaseGalleryDialogProps> = ({
  open,
  onClose,
  isLoadingCandidates,
  candidates,
  candidatePage,
  setCandidatePage,
  totalCandidates,
  selectedCandidates,
  setSelectedCandidates,
  candidateCaptions,
  setCandidateCaptions,
  candidateFileNames,
  setCandidateFileNames,
  onSave,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(8, 10, 14, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          backdropFilter: 'blur(24px)',
          color: '#fff',
          maxHeight: '90vh',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Manage Showcase Gallery
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        <Typography sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', mb: 3 }}>
          Select field-captured deployment or retrieval photos to showcase on the project overview page.
        </Typography>

        {isLoadingCandidates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress color="success" />
          </Box>
        ) : candidates.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '13px' }}>
              No candidate photos found in deployments.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
              {candidates.map((candidate) => {
                const isChecked = !!selectedCandidates[candidate.gcs_path];
                const captionValue = candidateCaptions[candidate.gcs_path] || '';
                return (
                  <Box
                    key={candidate.gcs_path}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      outline: isChecked ? '2px solid #10b981' : 'none',
                      '&:hover': { borderColor: 'rgba(16, 185, 129, 0.3)' },
                    }}
                  >
                    {/* Image Frame with Overlay Checkbox */}
                    <Box sx={{ position: 'relative', height: '140px', background: '#000' }}>
                      <img
                        src={candidate.view_url}
                        alt="Candidate"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: 'rgba(0,0,0,0.6)',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            setSelectedCandidates((prev) => ({
                              ...prev,
                              [candidate.gcs_path]: e.target.checked,
                            }));
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                      </Box>
                    </Box>

                    {/* Content details */}
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography
                        sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', fontWeight: 700 }}
                      >
                        {candidate.source.replace(/_/g, ' ')}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>
                        Deployment: {candidate.deployment_name}
                      </Typography>
                      <input
                        type="text"
                        placeholder="Add a caption..."
                        value={captionValue}
                        onChange={(e) => {
                          setCandidateCaptions((prev) => ({
                            ...prev,
                            [candidate.gcs_path]: e.target.value,
                          }));
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: '4px',
                          color: '#fff',
                          fontSize: '11px',
                          padding: '4px 8px',
                          outline: 'none',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Pagination controls */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={candidatePage === 1}
                onClick={() => setCandidatePage((prev) => Math.max(1, prev - 1))}
                sx={{ color: '#fff', borderColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                Previous
              </Button>
              <Typography sx={{ fontSize: '12px' }}>
                Page {candidatePage} of {Math.ceil(totalCandidates / 6)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={candidatePage >= Math.ceil(totalCandidates / 6)}
                onClick={() => setCandidatePage((prev) => prev + 1)}
                sx={{ color: '#fff', borderColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                Next
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <Box
        sx={{
          p: 2.5,
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
        }}
      >
        <Button size="small" onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSave}
          sx={{ bgcolor: '#10b981', color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#059669' } }}
        >
          Save Showcase
        </Button>
      </Box>
    </Dialog>
  );
};

export default ShowcaseGalleryDialog;
